// src/pages/XeroIntegration.tsx — Sprint 2 G03: per-item Push to Xero (QUEUED → SYNCED)
// CM-08: Sync history tab now reads from getXeroSync() push records
import { useState, useEffect } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { XERO_CONNECTION, ACCOUNT_MAPPINGS, SYNC_QUEUE, TAX_CODE_META, QUEUED_TOTAL, SKIPPED_COUNT, AccountMapping, SyncQueueItem, AUTaxCode } from '../utils/mockDataXero'
import { getAllXeroSyncs } from '../utils/mockDataProjectGaps'
import { api } from '../utils/api'
import { Link2, AlertTriangle, CheckCircle, Clock, RefreshCw, ExternalLink, Zap, X, Loader, Sparkles, Send } from 'lucide-react'

const XERO_API = 'https://trafficprocure-api.dirlayo69.workers.dev'

const fmtAUD  = (n: number) => new Intl.NumberFormat('en-AU', { style:'currency', currency:'AUD', minimumFractionDigits:2 }).format(n)
const fmtDate = (s?: string) => s ? new Date(s).toLocaleString('en-AU', { day:'numeric', month:'short', year:'2-digit', hour:'2-digit', minute:'2-digit' }) : '—'

async function fetchAISuggestion(category: string) {
  try {
    const r = await fetch('https://api.anthropic.com/v1/messages', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ model:'claude-sonnet-4-6', max_tokens:300, messages:[{ role:'user', content:`Xero account for Australian civil contractor category: "${category}". Return ONLY JSON: {"accountCode":"4XX","accountName":"name","taxCode":"G11","reason":"why"}` }] }) })
    const d = await r.json()
    return JSON.parse((d.content?.[0]?.text ?? '').trim())
  } catch { return null }
}

function SyncBadge({ status }: { status: string }) {
  const m: Record<string, string> = { QUEUED:'bg-blue-100 text-blue-700', SYNCED:'bg-green-100 text-green-700', SKIPPED:'bg-red-100 text-red-700', PENDING:'bg-amber-100 text-amber-700' }
  const l: Record<string, string> = { QUEUED:'Queued', SYNCED:'Synced ✓', SKIPPED:'Skipped', PENDING:'Pending' }
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${m[status] ?? 'bg-gray-100 text-gray-500'}`}>{l[status] ?? status}</span>
}

function MappingRow({ mapping, onUpdate }: { mapping: AccountMapping; onUpdate: (id: string, u: Partial<AccountMapping>) => void }) {
  const [loading, setLoading] = useState(false)
  const [ai, setAi] = useState<{accountCode:string;accountName:string;reason:string}|null>(null)
  const suggest = async () => {
    setLoading(true)
    const r = await fetchAISuggestion(mapping.categoryLabel)
    setLoading(false)
    if (r) { setAi(r); onUpdate(mapping.id, { aiReason: r.reason, aiSuggested: true }) }
  }
  const confirm = () => {
    if (ai) onUpdate(mapping.id, { xeroAccountCode: ai.accountCode, xeroAccountName: ai.accountName, aiSuggested: true, isConfirmed: true })
  }
  const isConf = mapping.isConfirmed
  return (
    <tr className={`border-b hover:bg-gray-50 ${isConf ? 'bg-green-50/30':''}`}>
      <td className="px-4 py-2.5"><div className="text-xs font-medium text-gray-900">{mapping.categoryLabel}</div><div className="text-[10px] text-gray-400">{mapping.procurementCategory}</div></td>
      <td className="px-3 py-2.5 text-xs text-gray-600">{mapping.trackingCategory ?? '—'}</td>
      <td className="px-3 py-2.5"><span className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded font-medium">{mapping.taxCode}</span></td>
      <td className="px-3 py-2.5 text-xs">{isConf ? <span className="text-green-700 font-semibold">{mapping.xeroAccountCode} — {mapping.xeroAccountName}</span> : ai ? <span className="text-blue-700">{ai.accountCode} — {ai.accountName}</span> : <span className="text-gray-400">{mapping.xeroAccountCode} — {mapping.xeroAccountName}</span>}</td>
      <td className="px-3 py-2.5 text-center"><span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${isConf?'bg-green-100 text-green-700':ai?'bg-blue-100 text-blue-700':'bg-gray-100 text-gray-500'}`}>{isConf?'✓ Confirmed':ai?'Suggested':'AI suggest'}</span></td>
      <td className="px-4 py-2.5 text-right">
        {!isConf && !loading && <button onClick={suggest} className="text-[10px] px-2 py-1 rounded bg-purple-100 text-purple-700 hover:bg-purple-200 font-medium mr-1"><Sparkles className="w-3 h-3 inline mr-0.5" />AI suggest</button>}
        {loading && <span className="text-[10px] text-purple-600"><Loader className="w-3 h-3 inline animate-spin mr-1" />Thinking…</span>}
        {ai && !isConf && <button onClick={confirm} className="text-[10px] px-2 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200 font-medium">Confirm</button>}
      </td>
    </tr>
  )
}

export function XeroIntegration() {
  const { isTier } = useAuthStore()
  const [tab, setTab]           = useState<'connection'|'mapping'|'queue'|'history'>('connection')
  const [connected, setConn]    = useState(XERO_CONNECTION.connected)
  const [connecting, setConning]= useState(false)
  const [tenantName, setTenant] = useState<string>('')
  const [xeroError, setXeroError] = useState<string>('')
  const [mappings, setMappings] = useState<AccountMapping[]>(ACCOUNT_MAPPINGS)
  const [queue, setQueue]       = useState<SyncQueueItem[]>([...SYNC_QUEUE])
  const [pushing, setPushing]   = useState<Record<string,boolean>>({})

  // Check real Xero connection status on mount
  useEffect(() => {
    fetch(`${XERO_API}/api/accounting/status`)
      .then(r => r.json())
      .then((d: any) => {
        if (d.connected) {
          setConn(true)
          setTenant(d.tenantName ?? '')
        }
      })
      .catch(() => {}) // silently fall back to mock state
  }, [])

  // Real OAuth popup flow
  const handleConnect = async () => {
    setConning(true)
    setXeroError('')
    const popup = window.open(
      `${XERO_API}/api/xero/auth`,
      'xero-oauth',
      'width=700,height=600,left=200,top=100'
    )
    if (!popup) {
      setXeroError('Pop-up blocked. Allow pop-ups for this site and try again.')
      setConning(false)
      return
    }
    const handler = (e: MessageEvent) => {
      if (e.data?.type === 'xero-connected') {
        setConn(true)
        setTenant(e.data.tenantName ?? '')
        setConning(false)
        window.removeEventListener('message', handler)
      } else if (e.data?.type === 'xero-error') {
        setXeroError(`Connection failed: ${e.data.error ?? 'unknown error'}`)
        setConning(false)
        window.removeEventListener('message', handler)
      }
    }
    window.addEventListener('message', handler)
    // Clean up if popup closes without posting a message
    const poll = setInterval(() => {
      if (popup.closed) {
        clearInterval(poll)
        window.removeEventListener('message', handler)
        setConning(false)
      }
    }, 500)
  }

  const handleDisconnect = async () => {
    await fetch(`${XERO_API}/api/xero/disconnect`, { method: 'POST' }).catch(() => {})
    setConn(false)
    setTenant('')
  }
  const updateMapping = (id: string, u: Partial<AccountMapping>) => setMappings(p => p.map(m => m.id===id?{...m,...u}:m))
  const pushItem = async (id: string) => {
    setPushing(p=>({...p,[id]:true}))
    await new Promise(r=>setTimeout(r,900+Math.random()*500))
    setPushing(p=>({...p,[id]:false}))
    setQueue(p=>p.map(q=>q.id===id?{...q,status:'SYNCED' as const}:q))
  }

  const queuedCount = queue.filter(q=>q.status==='QUEUED').length
  const syncedCount = queue.filter(q=>q.status==='SYNCED').length
  const confirmedCount = mappings.filter(m => m.isConfirmed).length

  return (
    <div className="space-y-5">
      <div><h1 className="text-2xl font-bold text-gray-900">Xero Integration</h1><p className="text-sm text-gray-500 mt-0.5">AI account mapping · per-invoice push to Xero · sync history</p></div>

      <div className="grid grid-cols-4 gap-3">
        {[
          { label:'Connection', value: connected?'Connected':'Disconnected', color: connected?'text-green-700':'text-red-600', sub: connected?(XERO_CONNECTION.tenantName ?? 'Connected'):'OAuth required' },
          { label:'Queue ready',      value: queuedCount, color: queuedCount>0?'text-blue-700':'text-gray-400', sub:'items awaiting push' },
          { label:'Synced this session',value: syncedCount, color: syncedCount>0?'text-green-700':'text-gray-400', sub:'posted to Xero' },
          { label:'Account mappings', value: `${confirmedCount}/${mappings.length}`, color:'text-gray-900', sub:'categories confirmed' },
        ].map(({ label, value, color, sub }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <div className={`text-xl font-bold ${color}`}>{value}</div>
            <div className="text-xs font-medium text-gray-700 mt-0.5">{label}</div>
            <div className="text-xs text-gray-400">{sub}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
        {(['connection','mapping','queue','history'] as const).map(t => (
          <button key={t} onClick={()=>setTab(t)} className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors flex-1 capitalize ${tab===t?'bg-white text-gray-900 shadow-sm':'text-gray-500 hover:text-gray-700'}`}>
            {t==='queue'?`📤 Sync queue${queuedCount>0?` (${queuedCount})`:''}`:t==='mapping'?'🧠 AI mapping':t==='connection'?'🔗 Connection':'📋 History'}
          </button>
        ))}
      </div>

      {tab==='connection' && (
        <div className={`rounded-xl border p-5 ${connected?'bg-green-50 border-green-200':'bg-white border-gray-200'}`}>
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${connected?'bg-green-100':'bg-gray-100'}`}>
              {connected?<CheckCircle className="w-6 h-6 text-green-600"/>:<Link2 className="w-6 h-6 text-gray-400"/>}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-1">Xero OAuth 2.0 connection{connected&&<span className="ml-2 text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full font-semibold">Active</span>}</h3>
              {connected
                ? <div className="text-sm text-gray-700 space-y-0.5"><div><span className="text-gray-500">Org:</span> <strong>{(XERO_CONNECTION as any).organisationName}</strong></div><div><span className="text-gray-500">Token expires:</span> {fmtDate((XERO_CONNECTION as any).tokenExpiresAt)}</div></div>
                : <p className="text-sm text-gray-600">Connect via OAuth 2.0 to enable invoice push, account mapping, and reconciliation.</p>}
            </div>
            {!connected
              ? <button onClick={handleConnect} disabled={connecting} className="btn-primary text-sm flex items-center gap-2 disabled:opacity-70">{connecting?<><Loader className="w-4 h-4 animate-spin"/>Connecting…</>:<><ExternalLink className="w-4 h-4"/>Connect to Xero</>}</button>
              : <button onClick={handleDisconnect} className="btn-secondary text-sm text-red-600">Disconnect</button>}
          </div>
        </div>
      )}

      {tab==='mapping' && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead><tr className="bg-gray-50 border-b text-gray-500 text-[10px] font-semibold uppercase tracking-wide"><th className="text-left px-4 py-3">Category</th><th className="text-left px-3 py-3">Supplier</th><th className="text-left px-3 py-3">Tax</th><th className="text-left px-3 py-3">Xero account</th><th className="text-center px-3 py-3">Status</th><th className="text-right px-4 py-3">Action</th></tr></thead>
            <tbody>{mappings.map(m=><MappingRow key={m.id} mapping={m} onUpdate={updateMapping}/>)}</tbody>
          </table>
        </div>
      )}

      {tab==='queue' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-gray-900">{queuedCount} item{queuedCount!==1?'s':''} queued · {fmtAUD(QUEUED_TOTAL)} total</div>
            <button onClick={()=>queue.filter(q=>q.status==='QUEUED').forEach(q=>pushItem(q.id))} disabled={!connected||queuedCount===0} className="btn-primary text-sm flex items-center gap-2 disabled:opacity-40"><RefreshCw className="w-4 h-4"/>Push all to Xero</button>
          </div>
          {syncedCount>0&&<div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-xs text-green-800 flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0"/><strong>{syncedCount} invoice{syncedCount!==1?'s':''}</strong>&nbsp;pushed to Xero this session.</div>}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-xs">
              <thead><tr className="bg-gray-50 border-b text-gray-500"><th className="text-left px-4 py-3 font-medium">Ref</th><th className="text-left px-3 py-3 font-medium">Supplier</th><th className="text-left px-3 py-3 font-medium">Description</th><th className="text-right px-3 py-3 font-medium">Amount</th><th className="text-center px-3 py-3 font-medium">Status</th><th className="text-right px-4 py-3 font-medium">Action</th></tr></thead>
              <tbody className="divide-y divide-gray-50">
                {queue.map(item=>(
                  <tr key={item.id} className={`hover:bg-gray-50 ${item.status==='SYNCED'?'bg-green-50/20':''}`}>
                    <td className="px-4 py-2.5"><div className="font-mono font-semibold text-gray-700">{item.internalRef}</div><div className="text-[10px] text-gray-400">{item.type.replace('_',' ')}</div></td>
                    <td className="px-3 py-2.5 text-gray-700">{item.supplierName}</td>
                    <td className="px-3 py-2.5 text-gray-600 max-w-xs truncate">{item.description}</td>
                    <td className="px-3 py-2.5 text-right font-mono font-semibold text-gray-900">{fmtAUD(item.amount+item.gst)}</td>
                    <td className="px-3 py-2.5 text-center"><SyncBadge status={item.status}/></td>
                    <td className="px-4 py-2.5 text-right">
                      {item.status==='QUEUED'&&<button onClick={()=>pushItem(item.id)} disabled={!connected||pushing[item.id]} className="text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 disabled:opacity-50 px-2.5 py-1 rounded-lg font-medium flex items-center gap-1 ml-auto">{pushing[item.id]?<><Loader className="w-3 h-3 animate-spin"/>Pushing…</>:<><Send className="w-3 h-3"/>Push</>}</button>}
                      {item.status==='SYNCED'&&<span className="text-[10px] text-green-600 flex items-center gap-1 justify-end"><CheckCircle className="w-3 h-3"/>Synced</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab==='history'&&(()=>{
        const syncs = getAllXeroSyncs()
        if (syncs.length===0) return (
          <div className="bg-white border border-gray-200 rounded-xl flex flex-col items-center py-16 text-center text-gray-400">
            <Clock className="w-10 h-10 text-gray-300 mb-3"/>
            <div className="text-sm font-medium text-gray-500">No sync history yet</div>
            <div className="text-xs text-gray-400 mt-1">Connect to Xero and push invoices to begin logging</div>
          </div>
        )
        return (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">{syncs.length} sync record{syncs.length!==1?'s':''}</span>
              <span className="text-xs text-gray-400">All progress claim pushes to Xero</span>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead><tr className="bg-gray-50 border-b text-gray-500 text-[10px] font-semibold uppercase tracking-wide">
                  <th className="text-left px-4 py-3">Claim ref</th>
                  <th className="text-left px-3 py-3">Job</th>
                  <th className="text-left px-3 py-3">Xero invoice</th>
                  <th className="text-left px-3 py-3">Pushed by</th>
                  <th className="text-left px-3 py-3">Date &amp; time</th>
                  <th className="text-center px-3 py-3">Status</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-50">
                  {syncs.map((s,i)=>(
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-mono font-semibold text-gray-700">{s.claimId}</td>
                      <td className="px-3 py-2.5 text-gray-600">{s.jobId}</td>
                      <td className="px-3 py-2.5 font-mono text-blue-700">{s.xeroInvoiceId}</td>
                      <td className="px-3 py-2.5 text-gray-600">{s.syncedByName}</td>
                      <td className="px-3 py-2.5 text-gray-500">{fmtDate(s.syncedAt)}</td>
                      <td className="px-3 py-2.5 text-center"><span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">Synced ✓</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      })()}
    </div>
  )
}
